import { Spin } from 'antd';
import startCase from 'lodash/startCase';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import Loader from 'components/molecules/Loader';
import { tpoOptionTestId, tpoPartTestId } from '../config/testIds';

export interface UseDimOptionsParams {
    dimension: string;
    attributeType: string;
    lowestAttribute?: boolean;
    filters?: Record<string, unknown>;
    selectedDimension?: { value: string | string[]; dimension: string; attributeType: string };
    enabled?: boolean;
    searchString?: string;
}

export interface UseDimOptionsResult {
    options: { label: string; value: string | number }[];
    isPending: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
}

export type UseDimOptionsHook = (params: UseDimOptionsParams) => UseDimOptionsResult;

export interface DimSelectProps {
    dimension: string;
    attributeType: string;
    lowestAttribute?: boolean;
    isMultiSelect?: boolean;
    placeholder?: string;
    selectedDimension?: { value: string | string[]; dimension: string; attributeType: string };
    useDimOptions: UseDimOptionsHook;
    enabled?: boolean;
    style?: React.CSSProperties;
    value?: string | string[];
    onChange?: (value: string | string[]) => void;
    /** Base id for QA locators. Child ids (options, select-all) derive from it. */
    testId?: string;
}

export const DimSelect = ({
    dimension,
    attributeType,
    lowestAttribute = false,
    isMultiSelect = false,
    placeholder,
    selectedDimension,
    useDimOptions,
    enabled = true,
    style,
    value,
    onChange,
    testId,
}: DimSelectProps) => {
    const [searchString, setSearchString] = useState('');

    const { options, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useDimOptions({
        dimension,
        attributeType,
        lowestAttribute,
        filters: {},
        selectedDimension: selectedDimension ?? {
            value: '',
            dimension,
            attributeType,
        },
        searchString,
        enabled,
    });

    const prevParentValueRef = useRef(selectedDimension?.value);
    const needsPruningRef = useRef(false);

    useEffect(() => {
        const prevVal = prevParentValueRef.current;
        const newVal = selectedDimension?.value;
        prevParentValueRef.current = newVal;

        if (!selectedDimension || !isMultiSelect) return;

        const prevArr = Array.isArray(prevVal) ? prevVal : prevVal ? [prevVal] : [];
        const newArr = Array.isArray(newVal) ? newVal : newVal ? [newVal] : [];
        const newSet = new Set(newArr.map(String));
        const parentRemoved = prevArr.length > 0 && prevArr.some((v) => !newSet.has(String(v)));

        if (parentRemoved) {
            needsPruningRef.current = true;
        }
    }, [selectedDimension?.value, isMultiSelect]);

    useEffect(() => {
        if (!needsPruningRef.current || isPending) return;

        needsPruningRef.current = false;

        if (!value || !Array.isArray(value) || value.length === 0) return;

        const validValues = new Set(options.map((o) => String(o.value)));
        const filteredValue = value.filter((v) => validValues.has(String(v)));

        if (filteredValue.length !== value.length) {
            onChange?.(filteredValue);
        }
    }, [isPending, options, value, onChange]);

    const selectedSet = useMemo(() => {
        const arr = Array.isArray(value) ? value : value ? [value] : [];
        return new Set(arr.map(String));
    }, [value]);

    const allLoadedSelected = useMemo(
        () => options.length > 0 && options.every((o) => selectedSet.has(String(o.value))),
        [options, selectedSet],
    );

    const handleSelectAll = useCallback(() => {
        if (!isMultiSelect) return;
        // Use the raw value array (preserving original types — string or number) so that
        // Ant Design Select can match them against option values with strict equality.
        const currentArr: Array<string | number> = Array.isArray(value)
            ? (value as Array<string | number>)
            : value
              ? [value as string | number]
              : [];

        if (allLoadedSelected) {
            const optionSet = new Set(options.map((o) => String(o.value)));
            onChange?.(currentArr.filter((v) => !optionSet.has(String(v))) as string[]);
        } else {
            const currentStrSet = new Set(currentArr.map(String));
            const toAdd = options
                .filter((o) => !currentStrSet.has(String(o.value)))
                .map((o) => o.value);
            onChange?.([...currentArr, ...toAdd] as string[]);
        }
    }, [isMultiSelect, options, allLoadedSelected, value, onChange]);

    const optionsWithTestIds = useMemo(
        () => (testId ? options.map((o) => ({ ...o, 'data-testid': tpoOptionTestId(testId, o.value) })) : options),
        [options, testId],
    );

    const renderDropdown = (menu: React.ReactNode) => (
        <>
            {isMultiSelect && (
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#0000000f]">
                    <span className="text-xs text-gray-400">
                        {selectedSet.size > 0 ? `${selectedSet.size} selected` : ''}
                    </span>
                    <button
                        className="text-xs font-medium text-blue-500 hover:text-blue-700 cursor-pointer"
                        data-testid={testId ? tpoPartTestId(testId, 'select_all') : undefined}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectAll();
                        }}
                    >
                        {allLoadedSelected ? 'Deselect All' : 'Select All'}
                    </button>
                </div>
            )}
            {menu}
            {hasNextPage && (
                <div className="flex justify-center items-center h-10">
                    {isFetchingNextPage && <Spin size="small" />}
                </div>
            )}
        </>
    );

    return (
        <SelectWithInfiniteScroller
            value={value}
            onChange={onChange}
            options={optionsWithTestIds}
            data-testid={testId ? tpoPartTestId(testId, 'select') : undefined}
            loading={isPending || isFetchingNextPage}
            hasMore={hasNextPage}
            onInfiniteScrollOnNextMemoised={fetchNextPage}
            style={style ?? { width: '100%' }}
            placeholder={placeholder ?? `Select ${startCase(attributeType)}`}
            filterOption={false}
            showArrow
            autoClearSearchValue={false}
            searchValue={searchString}
            showSearch
            mode={isMultiSelect ? 'multiple' : undefined}
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
            onSearch={(val: string) => {
                setSearchString(val);
            }}
            notFoundContent={
                isPending ? (
                    <div className="p-3">
                        <Loader />
                    </div>
                ) : (
                    <p>No Data</p>
                )
            }
            dropdownRender={renderDropdown}
        />
    );
};
